/**
 * The keyboard in front of the reader, and how the composer's shortcuts are
 * spelled on it.
 *
 * Read once at module load. Neither the platform nor the browser can change
 * under a running tab, so there is nothing here to subscribe to — unlike
 * `useIsMobile`, which watches a viewport that moves.
 *
 * One object per shortcut decides both what is bound and what is printed, so a
 * hint can never name a key that isn't listening. That matters more than it
 * sounds, because the key is not the same everywhere: see `EXPAND_COMPOSER`.
 */

/** A keyboard event, or the React synthetic one — only these four fields. */
interface KeyPress {
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}

export interface Shortcut {
  /**
   * One key per chip, in press order — the only way a shortcut is ever spelled
   * out to the reader. There was a `label` string here too, for `title`
   * attributes, until both tooltips were replaced by hints that slide out of the
   * control itself; nothing wanted the one-line form after that.
   */
  keys: string[];
  /** For `aria-keyshortcuts`, which wants the DOM's own key names. */
  aria: string;
  matches: (event: KeyPress) => boolean;
}

/**
 * iPadOS reports itself as a Mac here, which is the answer we want: the
 * keyboard it takes has a ⌘ on it. `platform` is deprecated and still the most
 * direct answer wherever it exists; the UA string is the fallback for the day
 * it goes.
 */
const apple =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

const mod = apple ? '⌘' : 'Ctrl';
const modAria = apple ? 'Meta' : 'Control';

/**
 * Either modifier counts. ⌘ is what a Mac is told to press and Ctrl is what
 * everyone else is told, but Ctrl+Enter is a deep habit on Mac too, and there
 * is no combination worth refusing here. The Windows key is not a false
 * positive risk: the desktop takes Win+Shift+letter before a page sees it.
 */
const held = (event: KeyPress) => event.metaKey || event.ctrlKey;

/**
 * ⌘E / Ctrl+E, and the history is worth keeping because the obvious answers were
 * both wrong.
 *
 * ⌘⇧E went first: some Chromium browsers open their own developer tooling on it,
 * and Firefox off macOS opens the devtools network panel on Ctrl+Shift+E. Both
 * are handled in browser chrome, above the page, where a `preventDefault` from
 * here cannot reach — and a shortcut a browser eats does not fail loudly, it just
 * does nothing while the hint beside it claims otherwise.
 *
 * ⌘⇧X replaced it (unbound everywhere, and what Microsoft Teams documents for
 * "expand compose box") and was dropped for feel: a three-key chord to make a
 * text box bigger.
 *
 * ⌘E has two conflicts and both are ones a page is allowed to win. On macOS it is
 * the Edit menu's "Use Selection for Find", which is an application command, not a
 * reserved browser one — every editor that binds ⌘E to something of its own
 * (inline code in Notion, preview in Obsidian) is doing exactly this. On Windows
 * and Linux, Ctrl+E focuses the address bar to search, and content that consumes
 * the key first keeps it, which is the same allowance every ⌘K command palette on
 * the web relies on.
 *
 * No Shift, so the matcher has to insist on its absence: a browser that eats ⌘⇧E
 * would otherwise hand us a key we already know we cannot have.
 */
const expandKeys = [mod, 'E'];

/** Toggles the composer between its one-line pill and the taller frame. */
export const EXPAND_COMPOSER: Shortcut = {
  keys: expandKeys,
  aria: `${modAria}+E`,
  matches: (event) => held(event) && !event.shiftKey && event.key.toUpperCase() === 'E',
};

/*
 * "⏎" (U+23CE) rather than the "↩" (U+21A9) Apple prints on the key itself.
 * U+21A9 has an emoji presentation, and every browser on macOS reaches for it:
 * the hint came out carrying a little blue keycap emoji next to a grey ⌘. U+23CE
 * has no emoji form to fall into, so it renders in the page's own ink.
 */
const sendKeys = [mod, apple ? '⏎' : 'Enter'];

/**
 * Sends from the expanded frame, where plain Enter has to stay a new line: the
 * reason to open a full-height editor is to write something with lines in it.
 */
export const SEND_MESSAGE: Shortcut = {
  keys: sendKeys,
  aria: `${modAria}+Enter`,
  matches: (event) => held(event) && !event.shiftKey && event.key === 'Enter',
};

/** Every document-level shortcut has to ask, and all of them answer the same way. */
export function isTyping(node: EventTarget | null): boolean {
  const el = node as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable === true;
}

/** Read off `aria-modal`, which every dialog already sets, so it covers unwritten ones too. */
export function isModalOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null;
}
